// filter all variables
const variableRegEx = /{{([^}]*)}}/g;

const checkCurlyBrackets = /{{2}([^{}]+){2}(?=\}{2})/g;

// filter only custom variables
const customVariablesRegEx = /{{(custom-variable\d+)}}/g;

// Match any valid hyphenated variable (lowercase letters, numbers, hyphens)
// This includes profile attributes like {{first-name}}, {{annual-income}}, {{preferred-location-1}}
const validVariableRegEx = /^{{[a-z0-9][a-z0-9-]*}}$/;

// validation patterns
const variableValidationPattern = [
  // /}}{{/g,
  /{{2}[^}]*[^}]}[^}]/g,
  /(.*?){{(.*?)}}}(.*?)/g,
  /(.*?){{{(.*?)}}(.*?)/g,
];

// get variables
export const getVariableValue = (variables, title) => {
  const res = variables?.filter((v) => v?.title === title);
  return res?.length > 0 ? res?.[0] : null;
};

// update variables
export const updateVariablesValue = (
  variables1,
  variables2 = [],
  title,
  value
) => {
  const res = variables1?.map((v) => {
    let variableValue = null;
    if (variables2?.length > 0)
      variableValue = variables2?.filter((v) => v?.title === title);
    if (v?.title === title)
      return { ...v, value: value || variableValue?.[0]?.value || '' };
    return v;
  });
  return res;
};

export const updateVariablesFromText = (value, variables1, variables2) => {
  let result = fliterWithCurlyBrackets(value);
  let list = [];

  result?.map((r, i) => {
    const formatedVariable = `${r}}}`;
    const data1 =
      variables1?.length > 0
        ? variables1?.filter((v) => v?.title === formatedVariable)
        : [];
    const data2 =
      variables2?.length > 0
        ? variables2?.filter((v) => v?.title === formatedVariable)
        : [];

    // Check if variable already exists in the variables list (profile attributes added via dropdown)
    const existsInVariables = data1?.length > 0 || data2?.length > 0;
    // Check if it's a valid custom variable pattern
    const isCustomVariable = checkIsoriginalVariable(formatedVariable);

    // Only process if it's a custom variable OR already exists in variables list
    if (isCustomVariable || existsInVariables) {
      const checkList = list?.filter((l) => l?.title === formatedVariable);

      if (data1?.length > 0 && checkList?.length === 0) {
        // Variable exists in variables1 (bodyVariables) - preserve it with metadata
        list.push({
          title: formatedVariable,
          value: data1?.[0]?.value,
          attribute_id: data1?.[0]?.attribute_id || null,
          type: data1?.[0]?.type || 'custom'
        });
      } else if (
        (data1?.length === 0 || !variables1) &&
        data2?.length > 0 &&
        checkList?.length === 0
      ) {
        // Variable exists in variables2 (headerVariable) - preserve it with metadata
        list.push({
          title: formatedVariable,
          value: data2?.[0]?.value,
          attribute_id: data2?.[0]?.attribute_id || null,
          type: data2?.[0]?.type || 'custom'
        });
      } else if (
        isCustomVariable &&
        data1?.length === 0 &&
        data2?.length === 0 &&
        checkList?.length === 0
      ) {
        // Only add NEW variables if they match custom-variable pattern
        // This prevents manually typed {{hello}} from being added
        list.push({
          title: formatedVariable,
          value: '',
          attribute_id: null,
          type: 'custom'
        });
      }
    }
  });
  return list;
};

// check variables
// Only allows custom variables ({{custom-variable1}}, {{custom-variable2}}, etc.)
// Profile attributes are validated separately through the existing variables list
export const checkIsoriginalVariable = (variable) => {
  // Only accept custom-variable pattern for manually typed variables
  if (variable?.toString()?.match(customVariablesRegEx)) {
    return true;
  }
  return false;
};

export const checkVariable = (value) =>
  value?.toString()?.toLowerCase()?.match(variableRegEx);

export const checkVariablesAreValid = (value) => {
  const res = variableValidationPattern?.map((p, i) => {
    var updatedStr = value?.toString().toLowerCase().match(p);
    return updatedStr?.length > 0 ? true : false;
  });
  return res?.filter((r) => r === true)?.length > 0 ? true : false;
};

// curlly brackets
export const fliterWithCurlyBrackets = (value) =>
  value?.toString()?.toLowerCase()?.match(checkCurlyBrackets);

export const addClosingBracket = (str) => {
  const regex = /\{+(?!})*$/g;
  return str.replaceAll(regex, `{{$1}}`);
};

// variables counts
export const getVariableCount = (value) =>
  value?.toString()?.toLowerCase()?.match(variableRegEx);

export const getCustomVariableCount = (value) =>
  value?.toString()?.toLowerCase()?.match(customVariablesRegEx);

export const getReplaceVariablesAndCount = (value) => {
  let count = 1;

  // Replace all valid variables with their count
  // This handles all variables including profile attributes like {{annual-income}}, {{preferred-location-1}}
  value = value?.replaceAll?.(variableRegEx, function (match, variable) {
    // Only count if it's a valid variable format
    const fullMatch = `{{${variable}}}`;
    if (fullMatch.match(validVariableRegEx)) {
      return `${count++}`;
    }
    return match;
  });

  return {
    count,
    value,
  };
};

export const replaceCustomVariableCount = (value) => {
  var count = 1;
  if (value?.length > 0)
    return value?.replaceAll(customVariablesRegEx, function (match, variable) {
      return `{{custom-variable${count++}}}`;
    });
  return value;
};

/**
 * Check for duplicate variables in text
 * @param {string} text - Template text
 * @returns {Array} - Array of duplicate variable names
 */
export const checkDuplicateVariables = (text) => {
  if (!text) return [];
  const matches = text.match(variableRegEx) || [];
  const seen = new Set();
  const duplicates = [];

  matches.forEach((v) => {
    const lowerV = v.toLowerCase();
    if (seen.has(lowerV)) {
      if (!duplicates.includes(lowerV)) {
        duplicates.push(lowerV);
      }
    }
    seen.add(lowerV);
  });

  return duplicates;
};

/**
 * Check if variables are at start or end of text
 * @param {string} text - Template text
 * @returns {Object} - { startsWithVariable: boolean, endsWithVariable: boolean, startVar: string|null, endVar: string|null }
 */
export const checkVariablePosition = (text) => {
  if (!text) return { startsWithVariable: false, endsWithVariable: false };

  const trimmedText = text.trim();
  const result = {
    startsWithVariable: false,
    endsWithVariable: false,
    startVar: null,
    endVar: null,
  };

  // Check if starts with variable
  const startMatch = trimmedText.match(/^{{([a-z0-9][a-z0-9-]*)}}/i);
  if (startMatch) {
    result.startsWithVariable = true;
    result.startVar = startMatch[1];
  }

  // Check if ends with variable
  const endMatch = trimmedText.match(/{{([a-z0-9][a-z0-9-]*)}}$/i);
  if (endMatch) {
    result.endsWithVariable = true;
    result.endVar = endMatch[1];
  }

  return result;
};
